import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Check if we're on different device sizes
  const isMobile = windowWidth <= 768;
  const isSmallScreen = windowWidth <= 576;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set loaded state after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section style={styles.heroSection} aria-label="About Dee Cee Elevators">
      {/* Blue Background with gradient */}
      <div style={styles.heroBackground}>
        <div style={styles.heroOverlay}>
          <div style={styles.blueprintGrid}></div>
        </div>
      </div>

      {/* Left accent circle */}
      <div style={styles.accentCircleLeft}></div>

      {/* Right accent circle */}
      <div style={styles.accentCircleRight}></div>

      {/* Content container */}
      <div
        style={{
          ...styles.heroContent,
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? "translateY(0)" : "translateY(20px)",
          padding: isMobile ? "0 20px" : "0 5%",
        }}
      >
        {/* Tagline */}
        <div style={styles.taglineWrapper}>
          <span style={styles.tagline}>
            <i className="fas fa-building" style={styles.taglineIcon}></i>
            OUR STORY, OUR MISSION
          </span>
        </div>

        {/* Main heading with highlight */}
        <h1
          style={{
            ...styles.heading,
            fontSize: isMobile
              ? windowWidth <= 480
                ? "2.5rem"
                : "2.8rem"
              : "4.5rem",
          }}
        >
          Our Journey to
          <span style={styles.headingHighlight}>Excellence</span>
        </h1>

        {/* Subtitle */}
        <p
          style={{
            ...styles.subtitle,
            fontSize: isMobile ? "1.1rem" : "1.3rem",
            marginBottom: isMobile ? "30px" : "50px",
          }}
        >
          Founded in 2012 with a vision to revolutionize vertical
          transportation, DeeCee Elevators has grown from a small team of
          passionate engineers to an industry leader with over 500 successful
          installations across North India.
        </p>

        {/* Call to action buttons */}
        <div
          style={{
            ...styles.ctaContainer,
            flexDirection: isSmallScreen ? "column" : "row",
            width: isSmallScreen ? "100%" : "auto",
            maxWidth: isSmallScreen ? "300px" : "none",
          }}
        >
          <Link
            to="/gallery"
            style={{
              ...styles.primaryButton,
              width: isSmallScreen ? "100%" : "auto",
            }}
          >
            <i className="fas fa-images" style={styles.buttonIcon}></i>
            Explore Our Gallery
          </Link>

          <Link
            to="/contact"
            style={{
              ...styles.secondaryButton,
              width: isSmallScreen ? "100%" : "auto",
            }}
          >
            Get In Touch
            <i
              className="fas fa-handshake"
              style={{
                ...styles.buttonIcon,
                marginLeft: "10px",
                marginRight: 0,
              }}
            ></i>
          </Link>
        </div>
      </div>

      {/* Blueprint decoration line - left */}
      <div
        style={{
          ...styles.blueprintLineLeft,
          display: isMobile ? "none" : "block",
        }}
      ></div>

      {/* Blueprint decoration line - right */}
      <div
        style={{
          ...styles.blueprintLineRight,
          display: isMobile ? "none" : "block",
        }}
      ></div>

      {/* Scroll indicator */}
      {!isMobile && (
        <div
          style={{
            ...styles.scrollIndicator,
            opacity: isLoaded ? 1 : 0,
          }}
          onClick={() =>
            window.scrollTo({
              top: window.innerHeight - 80,
              behavior: "smooth",
            })
          }
        >
          <i className="fas fa-chevron-down" style={styles.scrollIcon}></i>
        </div>
      )}

      {/* Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes pulse {
            0% { transform: translateY(0); }
            50% { transform: translateY(5px); }
            100% { transform: translateY(0); }
          }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes float {
            0% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0); }
          }
        `}
      </style>
    </section>
  );
};

// Styles
const styles = {
  heroSection: {
    height: "100vh",
    minHeight: "600px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    color: "white",
  },
  heroBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(135deg, #01162E 0%, #052447 45%, #0A3A70 100%)", // Darker blue gradient
    backgroundSize: "cover",
    zIndex: 0,
  },
  heroOverlay: {
    position: "absolute",
    width: "100%",
    height: "100%",
    background:
      "radial-gradient(circle at 30% 40%, rgba(20, 90, 170, 0.15) 0%, rgba(10, 50, 120, 0) 70%)", // Subtle blue radial gradient
    zIndex: 1,
  },
  blueprintGrid: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundImage: `
      linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px)
    `,
    backgroundSize: "30px 30px",
    opacity: 0.6,
  },
  accentCircleLeft: {
    position: "absolute",
    top: "20%",
    left: "5%",
    width: "200px",
    height: "200px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(65, 160, 240, 0.08) 0%, rgba(3, 80, 160, 0) 70%)", // Light blue accent
    zIndex: 2,
    opacity: 0.7,
  },
  accentCircleRight: {
    position: "absolute",
    top: "15%",
    right: "7%",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(55, 140, 230, 0.1) 0%, rgba(20, 80, 180, 0) 70%)", // Another blue accent
    zIndex: 2,
  },
  blueprintLineLeft: {
    position: "absolute",
    top: "30%",
    left: "15%",
    width: "1px",
    height: "40%",
    background:
      "linear-gradient(to bottom, rgba(80, 150, 230, 0.05), rgba(80, 150, 230, 0.2), rgba(80, 150, 230, 0.05))", // Blue line gradient
    zIndex: 2,
  },
  blueprintLineRight: {
    position: "absolute",
    top: "35%",
    right: "15%",
    width: "1px",
    height: "40%",
    background:
      "linear-gradient(to bottom, rgba(80, 150, 230, 0.05), rgba(80, 150, 230, 0.2), rgba(80, 150, 230, 0.05))", // Blue line gradient
    zIndex: 2,
  },
  heroContent: {
    position: "relative",
    zIndex: 3,
    maxWidth: "1000px",
    width: "100%",
    transition: "opacity 0.8s ease, transform 0.8s ease",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  taglineWrapper: {
    marginBottom: "30px",
    display: "flex",
    justifyContent: "center",
  },
  tagline: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.9rem",
    fontWeight: "600",
    letterSpacing: "2px",
    backgroundColor: "rgba(50, 120, 220, 0.15)", // Darker blue background with low opacity
    padding: "8px 16px",
    borderRadius: "30px",
    color: "#ffffff",
    textTransform: "uppercase",
    boxShadow: "0 4px 12px rgba(30, 100, 200, 0.2)", // Blue shadow
    border: "1px solid rgba(50, 120, 200, 0.3)", // Blue border
  },
  taglineIcon: {
    marginRight: "10px",
    fontSize: "0.8rem",
  },
  heading: {
    fontWeight: "800",
    lineHeight: "1.1",
    marginBottom: "30px",
    color: "#ffffff",
  },
  headingHighlight: {
    display: "block",
    color: "#4F9DF3", // Adjusted light blue highlight for better contrast on dark background
    textShadow: "0 0 15px rgba(30, 120, 230, 0.3)", // Blue glow
    marginTop: "15px",
  },
  subtitle: {
    fontWeight: "300",
    lineHeight: "1.6",
    color: "rgba(255, 255, 255, 0.9)",
    maxWidth: "700px",
  },
  ctaContainer: {
    display: "flex",
    gap: "30px",
    justifyContent: "center",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 35px",
    height: "55px",
    backgroundColor: "#1976D2", // Darker vibrant blue
    color: "#ffffff",
    fontSize: "1.1rem",
    fontWeight: "600",
    borderRadius: "8px",
    textDecoration: "none",
    boxShadow: "0 8px 20px rgba(20, 80, 180, 0.3)", // Blue shadow
    transition: "all 0.3s ease",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 35px",
    height: "55px",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    color: "#ffffff",
    fontSize: "1.1rem",
    fontWeight: "600",
    borderRadius: "8px",
    textDecoration: "none",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
  },
  buttonIcon: {
    fontSize: "1.1rem",
    marginRight: "10px",
  },
  scrollIndicator: {
    position: "absolute",
    bottom: "30px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 3,
    transition: "all 0.3s ease, opacity 1s ease",
    animation: "pulse 2s infinite",
    backdropFilter: "blur(5px)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
  scrollIcon: {
    color: "#ffffff",
    fontSize: "1rem",
  },
};

export default HeroSection;
